/* eslint-disable @typescript-eslint/no-explicit-any */
import { Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { EncryptionService } from './encryption.service';
import { assets } from '../core/constants/assets.const';
import { environment } from '../../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import {
  parseNotificationPayload,
  resolveCustomSoundFromPushData,
} from '../core/helpers/notification-sound.helper';
import { AudioService } from './audio.service';
import { I18nService } from './i18n.service';

@Injectable({
  providedIn: 'root',
})
export class MessagingService {
  assetsConst = assets;

  private receivedMessageIds = new Set<string>();
  /** Prevents double-play when SW notifies from both push + onBackgroundMessage. */
  private lastSoundPlayedAt = 0;

  // ✅ RxJS BehaviorSubject instead of Angular signal
  private _message = new BehaviorSubject<any | null>(null);
  readonly currentMessage: Observable<any | null> = this._message.asObservable();

  private audioService = inject(AudioService);

  constructor(
    private encryption: EncryptionService,
    private translate: I18nService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  /**
   * Initialize Firebase safely
   */
  private async getFirebaseMessaging() {
    const { initializeApp, getApps } = await import('firebase/app');
    const { getMessaging } = await import('firebase/messaging');

    const app = getApps().length ? getApps()[0] : initializeApp(environment.firebaseConfig);

    return getMessaging(app);
  }

  /**
   * Request notification permission
   */
  async requestPermission(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    const { getToken } = await import('firebase/messaging');
    const messaging = await this.getFirebaseMessaging();

    navigator.serviceWorker.ready
      .then(registration =>
        getToken(messaging, {
          vapidKey: environment.firebaseConfig.vapidKey,
          serviceWorkerRegistration: registration,
        }),
      )
      .then(currentToken => {
        if (currentToken) {
          this.encryption.encryptLocalStorage(currentToken, 'fcmToken');
        } else {
          alert(this.translate.translate('common.fcmNotification'));
        }
      })
      .catch(err => console.log('Token error:', err));
  }

  /**
   * Listen for foreground messages
   */
  async listenForFrontMessages(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    const { onMessage } = await import('firebase/messaging');
    const messaging = await this.getFirebaseMessaging();

    if (localStorage[environment.storageKey]) {
      onMessage(messaging, payload => {
        const messageId = payload.messageId;

        if (!messageId) {
          console.warn('Message without ID');
          return;
        }

        if (this.receivedMessageIds.has(messageId)) {
          console.log('Duplicate notification ignored:', messageId);
          return;
        }

        this.receivedMessageIds.add(messageId);

        const data = (payload.data ?? {}) as Record<string, unknown>;
        if (!payload.data) {
          return;
        }

        // FCM data.payload is a JSON string — parse, then coerce custom_sound to boolean.
        const payloadData = parseNotificationPayload(data['payload']);
        const shouldPlaySound = resolveCustomSoundFromPushData(data);

        this.applyPushSound(shouldPlaySound, payloadData['type'] ?? data['type']);
        this.displayNotification((data['title'] as string) || 'New Notification', {
          body: (data['body'] as string) || 'Click to view',
          icon: this.assetsConst.logo,
          data: payload.data,
          silent: !shouldPlaySound,
        });

        this._message.next({ action: 'refresh' });
      });
    }
  }

  private displayNotification(title: string, options: NotificationOptions): void {
    const currentLang = this.translate.getCurrentLang() || 'en';
    const isRtl = this.isRtlLanguage(currentLang);

    options = {
      ...options,
      dir: isRtl ? 'rtl' : 'ltr',
    };

    navigator.serviceWorker
      .getRegistration('/firebase-cloud-messaging-push-scope')
      .then(registration => {
        if (registration) {
          registration.showNotification(title, options);
        }
      });
  }

  /**
   * Per-push sound decision only — no sticky mute flag.
   * `true` → play; `false` → skip.
   * Debounced because SW may notify from both `push` and `onBackgroundMessage`.
   */
  applyPushSound(shouldPlaySound: boolean, type: unknown): void {
    if (!shouldPlaySound) {
      return;
    }

    const now = Date.now();
    if (now - this.lastSoundPlayedAt < 1000) {
      return;
    }
    this.lastSoundPlayedAt = now;

    if (type === 'CUSTOM') {
      this.playNotificationSoundCustom();
      return;
    }

    this.playNotificationSound();
  }

  private playNotificationSound(): void {
    this.audioService.play('notification');
  }

  private playNotificationSoundCustom(): void {
    this.audioService.play('notification');
  }

  private isRtlLanguage(lang: string): boolean {
    return ['ar'].includes(lang);
  }

  sendMessage(message: any) {
    this._message.next(message);
  }
}
